import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { Spinner } from "@/components/spinner";
import { StatusBadge } from "@/components/status-badge";
import { ThinkingTrace } from "@/components/thinking-trace";
import { ToolChips } from "@/components/tool-chips";
import { tools } from "@/lib/tools";
import { useAgentStream } from "@/lib/use-agent-stream";

export const Route = createFileRoute("/_authed/agents/$runId")({
  // Prefetch, not ensure: an unknown run is a 404 now, and the component
  // shows that as "nicht gefunden" rather than the router's error screen.
  loader: ({ context, params }) =>
    context.queryClient.prefetchQuery(
      context.tools.queryOptions("GET /api/agents/{id}", { id: params.runId }),
    ),
  component: RunDetail,
});

function RunDetail() {
  const { runId } = Route.useParams();
  const queryClient = useQueryClient();

  // TanStack Query owns the run record; SSE owns the live token stream.
  // Neither tries to be the other.
  const run = useQuery(tools.queryOptions("GET /api/agents/{id}", { id: runId }));
  const isLive = run.data?.status === "queued" || run.data?.status === "running";
  const stream = useAgentStream(runId, Boolean(run.data));

  // When the stream ends, pull the authoritative row once. This must be an
  // effect, not a render-body call — refetching during render re-enters the
  // render loop and starves navigation. Also invalidate the sidebar's run
  // list so its status badge doesn't stay stuck on "Queued"/"Running".
  const { refetch } = run;
  const streamClosed = stream.closed;
  useEffect(() => {
    if (streamClosed && isLive) {
      void refetch();
      void queryClient.invalidateQueries({ queryKey: tools.queryKey("GET /api/agents") });
    }
  }, [streamClosed, isLive, refetch, queryClient]);

  if (run.isPending) return <Spinner />;
  if (!run.data) {
    return (
      <div className="p-4 text-center md:p-8">
        <p className="text-muted-foreground text-sm">Ausführung nicht gefunden.</p>
        <Link to="/agents" className="text-sm underline decoration-accent underline-offset-4">
          Zurück zu den Unterhaltungen
        </Link>
      </div>
    );
  }

  const output = stream.text || run.data.output || "";

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 p-4 md:p-8">
      <div className="flex items-center gap-3">
        <Link to="/agents" className="text-muted-foreground text-sm hover:text-foreground">
          ← Unterhaltungen
        </Link>
        <StatusBadge status={run.data.status} />
      </div>

      <div className="rounded-lg border p-4">
        <h2 className="font-medium text-sm">Anfrage</h2>
        <p className="mt-1 text-muted-foreground text-sm">{run.data.prompt}</p>
      </div>

      <ToolChips events={stream.events} />

      <ThinkingTrace text={stream.thinking} done={stream.closed} />

      <div className="rounded-lg border p-4" data-testid="agent-output">
        <h2 className="font-medium text-sm">Antwort</h2>
        {output ? (
          <pre className="mt-2 whitespace-pre-wrap text-sm">{output}</pre>
        ) : (
          <p className="mt-2 text-muted-foreground text-sm">Warten auf Tycho…</p>
        )}
      </div>

      {run.data.error || stream.error ? (
        <p role="alert" className="text-destructive text-sm">
          {run.data.error ?? stream.error}
        </p>
      ) : null}
    </div>
  );
}
